import type { TFunction } from 'i18next';
import type {
  RefinementCtx,
  ZodArray,
  ZodNumber,
  ZodString,
  ZodType,
} from 'zod';
import type {
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
} from '../../../lib/flotiq-client/types/api-response';

import { z } from 'zod';

const addSchemaToGeoInputs = (
  validator: ZodNumber,
  key: string,
  t: TFunction,
  isRequired: boolean,
) => {
  const max = key === 'lon' ? 180 : 90;
  const transKey = key === 'lon' ? 'Longitude' : 'Latitude';
  const message = t('ContentForm.Errors.Coordinates', {
    coordinate: t(`ContentForm.${transKey}`),
    max,
    min: -max,
  });
  validator = validator.min(-max, message);
  validator = validator.max(max, message);
  return isRequired ? validator : validator.optional();
};

const createErrors = (
  context: RefinementCtx,
  idx: number,
  propName: string,
  value: unknown,
  t: TFunction,
  propSubName = '',
) => {
  [idx, idx + 1].forEach((currentIdx) =>
    context.issues.push({
      code: 'custom',
      path: [currentIdx, propName, ...(propSubName ? [propSubName] : [])],
      message: t('ContentForm.Errors.Unique'),
      input: value,
    }),
  );
};

const getUniquenessForObject = (
  context: RefinementCtx,
  element: Record<string, Record<string, unknown>>,
  propName: string,
  currentValue: Record<string, Record<string, unknown>>[],
  idx: number,
  t: TFunction,
) => {
  const notUniqueSubFields: { subField: string; value: unknown }[] = [];
  Object.keys(element[propName]).forEach((propSubName) => {
    if (
      element[propName][propSubName] ===
      currentValue[idx + 1][propName][propSubName]
    ) {
      notUniqueSubFields.push({
        subField: propSubName,
        value: element[propName][propSubName],
      });
    }
  });
  if (
    notUniqueSubFields.length &&
    notUniqueSubFields.length === Object.keys(element[propName]).length
  ) {
    notUniqueSubFields.forEach(({ subField, value }) =>
      createErrors(context, idx, propName, value, t, subField),
    );
  }
};

const checkUniqueness = (
  context: RefinementCtx,
  value: Record<string, Record<string, unknown>>[],
  uniqueFields: Record<string, boolean>,
  t: TFunction,
) => {
  value.forEach((element, idx, currentValue) => {
    if (idx + 1 >= currentValue.length) return;
    Object.keys(element).forEach((propName) => {
      if (!uniqueFields[propName] || !currentValue[idx + 1][propName]) return;
      if (typeof element[propName] === 'object') {
        getUniquenessForObject(
          context,
          element,
          propName,
          currentValue,
          idx,
          t,
        );
      } else {
        if (element[propName] === currentValue[idx + 1][propName]) {
          createErrors(context, idx, propName, element[propName], t);
        }
      }
    });
  });
};

const generateArrayObjectsShape = (
  arraySchema: ContentTypePropertySchema,
  arrayProperties: ContentTypePropertyConfig,
  t: TFunction,
  isPatchable: boolean,
) => {
  if (arraySchema.items?.properties) {
    return generateZodSchemaForCto(
      arraySchema.items.properties,
      t,
      arraySchema.items.required || [],
      arrayProperties?.items?.propertiesConfig
        ? arrayProperties.items.propertiesConfig
        : {},
      undefined,
      isPatchable,
    );
  } else if (arrayProperties.validation?.relationContenttype) {
    return z.object({
      dataUrl: z
        .string()
        .regex(
          new RegExp(`^.*${arrayProperties.validation.relationContenttype}.*$`),
          t('ContentForm.Errors.DataUrl'),
        ),
    });
  } else if (arraySchema.items?.$ref) {
    return z.object({
      dataUrl: z.string(),
    });
  }

  let validator = z.string();
  if (arraySchema.items?.pattern) {
    validator = validator.regex(
      new RegExp(arraySchema.items.pattern),
      t('ContentForm.Errors.Pattern', { pattern: arraySchema.items.pattern }),
    );
  }

  return validator;
};

const generateArrayShape = (
  arraySchema: ContentTypePropertySchema,
  arrayProperties: ContentTypePropertyConfig,
  t: TFunction,
  isPatchable: boolean,
  isRequired: boolean,
) => {
  let validator = z.array(
    generateArrayObjectsShape(arraySchema, arrayProperties, t, isPatchable),
  );

  let minItems = arraySchema.minItems;
  if (isRequired) minItems = 1;

  if (minItems && !isPatchable) {
    validator = validator.min(minItems, t('ContentForm.Errors.MinLength'));
  }
  if (
    arrayProperties?.validation &&
    !arrayProperties.validation.relationMultiple
  ) {
    validator = validator.max(1, t('ContentForm.Errors.MaxLength'));
  }

  if (arrayProperties?.inputType === 'object') {
    const properties = arrayProperties.items?.propertiesConfig || {};
    const uniqueFields = Object.keys(properties).reduce(
      (acc, propName) => {
        acc[propName] = properties[propName].unique || false;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    validator = validator.superRefine((value, ctx) => {
      if (!value || value.length <= 1) return;
      checkUniqueness(
        ctx,
        value as Record<string, Record<string, unknown>>[],
        uniqueFields,
        t,
      );
    }) as any;
  }
  return validator;
};

const generateShapeFromProperties = (
  fieldProperties: ContentTypePropertyConfig,
  validator: ZodType,
  t: TFunction,
) => {
  if (['select', 'radio'].indexOf(fieldProperties.inputType) > -1) {
    const optionKeys =
      (fieldProperties.useOptionsWithLabels
        ? fieldProperties.optionsWithLabels?.map((option) => option.value)
        : fieldProperties.options) || [];

    if (fieldProperties.isMultiple || fieldProperties.multiple) {
      validator = (validator as ZodArray<ZodString>).refine(
        (value) =>
          value.length === 0 ||
          !(value || []).find((option: string) => !optionKeys.includes(option)),
        {
          error: t('ContentForm.Errors.OptionNotFound'),
        },
      );
    } else {
      validator = (validator as ZodString).refine(
        (value) => !value || optionKeys.includes(value),
        {
          error: t('ContentForm.Errors.OptionNotFound'),
        },
      );
    }
  }
  if (fieldProperties.inputType === 'richtext') {
    validator = validator.refine((value) => value !== '<p><br></p>', {
      error: t('ContentForm.Errors.Required'),
    });
  }
  return validator;
};

const generateShapeFromSchema = (
  fieldSchema: ContentTypePropertySchema,
  fieldProperties: ContentTypePropertyConfig,
  t: TFunction,
  isPatchable: boolean,
  isRequired: boolean,
) => {
  if (fieldSchema.type === 'object') {
    return generateZodSchemaForCto(
      fieldSchema.properties || {},
      t,
      fieldSchema.required || [],
      {},
      fieldProperties?.inputType === 'geo',
      isPatchable,
    );
  } else if (fieldSchema.type === 'array') {
    return generateArrayShape(
      fieldSchema,
      fieldProperties,
      t,
      isPatchable,
      isRequired,
    );
  } else if (fieldProperties?.inputType === 'email') {
    return isRequired
      ? z.email(t('ContentForm.Errors.Email'))
      : z.union([z.literal(''), z.email(t('ContentForm.Errors.Email'))]);
  } else if (fieldSchema.type === 'number') {
    return z.number();
  } else if (fieldSchema.type === 'boolean') {
    return z.boolean();
  }

  let validator = z.string();
  if (fieldSchema.pattern) {
    validator = validator.regex(
      new RegExp(fieldSchema.pattern),
      t('ContentForm.Errors.Pattern', { pattern: fieldSchema.pattern }),
    );
  }
  if (isRequired)
    validator = validator.min(1, t('ContentForm.Errors.Required'));
  return validator;
};

export const generateZodSchemaForCto = (
  schema: Record<string, ContentTypePropertySchema>,
  t: TFunction,
  required: Record<string, boolean> | string[],
  properties: Record<string, ContentTypePropertyConfig>,
  isGeo = false,
  isPatchable = false,
) => {
  let requiredFields: Record<string, boolean> = {};
  if (!isPatchable && required) {
    requiredFields = Array.isArray(required)
      ? required.reduce(
          (acc, key) => {
            acc[key] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        )
      : required;
  }

  return z.object(
    Object.keys(schema).reduce(
      (prop, key) => {
        let validator: ZodType = generateShapeFromSchema(
          schema[key],
          properties[key],
          t,
          isPatchable,
          requiredFields[key],
        );
        if (properties[key]) {
          validator = generateShapeFromProperties(
            properties[key],
            validator,
            t,
          );
        }
        if (isGeo) {
          validator = addSchemaToGeoInputs(
            validator as ZodNumber,
            key,
            t,
            requiredFields[key],
          );
        }

        if (!requiredFields[key]) {
          validator = validator.optional();
        }

        prop[key] = validator;
        return prop;
      },
      {} as Record<string, ZodType>,
    ),
  );
};
